import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../../slices/authSlice';
import { jwtDecode } from 'jwt-decode';
import PDIcn from './pdIcons';
import FilesTab from './FilesTab';
import SettingsTab from './SettingsTab';
import AssignPopover from './AssignPopover';
import { avatarColor, initialsOf, relTime, renderBody } from '../Editor/commentHelpers';
import {
    ext, fileClass, ownerInitials, avatarPalette,
    formatDue, dueClass, progressOf, statusFor, langCode, projectName,
} from '../AllProjects/projectHelpers';

const fmtDateShort = (d) => {
    if (!d) return '';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const splitCrumb = (cat) => {
    if (!cat) return [];
    // Split "Marketing · Releases" or "Marketing / Releases"
    return cat.split(/[·/]/).map((s) => s.trim()).filter(Boolean);
};

const ownerDisplayName = (cb) => {
    if (!cb) return '';
    if (typeof cb === 'string') return '';
    const n = `${cb.firstName || ''} ${cb.lastName || ''}`.trim();
    return n || cb.email || '';
};

const LANG_FULL = {
    en: 'English', 'en-gb': 'English (UK)', 'en-us': 'English (US)',
    de: 'German', fr: 'French', es: 'Spanish', it: 'Italian',
    pt: 'Portuguese', ja: 'Japanese', zh: 'Chinese', ko: 'Korean',
    nl: 'Dutch', sv: 'Swedish', no: 'Norwegian', da: 'Danish',
    pl: 'Polish', ru: 'Russian', ar: 'Arabic',
};

const langDisplay = (code) => {
    if (!code) return '—';
    const k = code.toString().toLowerCase();
    return LANG_FULL[k] || code;
};

const TABS = ['Overview', 'Segments', 'Files', 'Comments', 'History', 'Settings'];

const wordCountFromSections = (sections = []) => sections.reduce(
    (s, sec) => s + (sec || '').split(/\s+/).filter(Boolean).length,
    0,
);

const toDateInputValue = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const MT_ENGINES = [
    { value: 'deepl',  label: 'DeepL' },
    { value: 'google', label: 'Google Translate' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'azure',  label: 'Azure Translator' },
];

// `onDelete` receives a scope: 'group' (every language) or 'language' (just this one).
// `languageCount` (default 1) is used to decide whether to show the per-language
// option at all — if the project group only has one language there's no
// meaningful difference between the two delete modes.
const ProjectQuickSettings = ({ project, onSave, onArchive, onDelete, onClose, languageCount = 1 }) => {
    const [name, setName] = useState(project.projectName || project.name || '');
    const [deadline, setDeadline] = useState(toDateInputValue(project.deadline));
    const [engine, setEngine] = useState(project.translationService || 'deepl');
    const [autoAssign, setAutoAssign] = useState(project.autoAssign !== false);
    const [saving, setSaving] = useState(false);
    // confirmingDelete is now a string scope ('group' | 'language') so we know
    // which confirm prompt is open. null = no prompt visible.
    const [confirmingDelete, setConfirmingDelete] = useState(null);
    const isArchived = !!project.archivedAt;
    const showLanguageDelete = languageCount > 1;

    const handleSave = async () => {
        const updates = {};
        if (name.trim() && name.trim() !== (project.projectName || '')) updates.projectName = name.trim();
        if (deadline !== toDateInputValue(project.deadline)) {
            updates.deadline = deadline ? new Date(deadline).toISOString() : null;
        }
        if (engine !== (project.translationService || 'deepl')) updates.translationService = engine;
        if (autoAssign !== (project.autoAssign !== false)) updates.autoAssign = autoAssign;
        if (Object.keys(updates).length === 0) { onClose(); return; }
        setSaving(true);
        try { await onSave(updates); onClose(); } finally { setSaving(false); }
    };

    return (
        <>
            <div className="assign-overlay" onClick={onClose} />
            <div
                className="assign-pop"
                style={{ right: 0, left: 'auto', minWidth: 320 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ap-h">
                    <div className="ap-title"><b>Quick settings</b></div>
                    <button type="button" className="ap-x" onClick={onClose} aria-label="Close">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                <div className="qs-field">
                    <label>Project name</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </div>

                <div className="qs-field">
                    <label>Due date</label>
                    <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
                </div>

                <div className="qs-field">
                    <label>Machine translation engine</label>
                    <select value={engine} onChange={(e) => setEngine(e.target.value)}>
                        {MT_ENGINES.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </select>
                </div>

                <label className="qs-toggle">
                    <div className="qs-toggle-text">
                        <b>Auto-assign translators</b>
                        <span>Route new languages to matching linguists automatically.</span>
                    </div>
                    <input
                        type="checkbox"
                        checked={autoAssign}
                        onChange={(e) => setAutoAssign(e.target.checked)}
                    />
                </label>

                <div className="ap-foot" style={{ gap: 8 }}>
                    <button
                        type="button"
                        className="ap-unassign"
                        onClick={() => onArchive(!isArchived)}
                    >
                        {isArchived ? 'Unarchive' : 'Archive'}
                    </button>
                    {confirmingDelete ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                                {confirmingDelete === 'language'
                                    ? 'Delete this language only?'
                                    : `Delete project${showLanguageDelete ? ' and all languages' : ''}?`}
                            </span>
                            <button
                                type="button"
                                className="ap-unassign"
                                style={{ background: '#fbe1e1', color: '#b53e3e' }}
                                onClick={() => onDelete(confirmingDelete)}
                            >
                                Confirm
                            </button>
                            <button
                                type="button"
                                className="cm-mini ghost"
                                onClick={() => setConfirmingDelete(null)}
                            >
                                Cancel
                            </button>
                        </span>
                    ) : (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                            {/* Per-language delete — only meaningful when there's more
                                than one language in the group. Hidden otherwise to
                                avoid redundant UI. */}
                            {showLanguageDelete && (
                                <button
                                    type="button"
                                    className="ap-unassign"
                                    onClick={() => setConfirmingDelete('language')}
                                    title="Delete only this language; other languages stay"
                                >
                                    Delete language
                                </button>
                            )}
                            <button
                                type="button"
                                className="ap-unassign"
                                onClick={() => setConfirmingDelete('group')}
                                title={showLanguageDelete
                                    ? 'Delete the whole project and every language in it'
                                    : 'Delete the project permanently'}
                            >
                                Delete project
                            </button>
                        </span>
                    )}
                    <span style={{ flex: 1 }} />
                    <button type="button" className="cm-mini ghost" onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className="cm-mini send"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </>
    );
};

// PushToGithubButton — visible only for projects imported via GitHub
// (project.githubSync.repo set). Posts to /api/integrations/github/projects/:id/push
// and reflects state inline: idle → pushing → result (PR link or error).
const PushToGithubButton = ({ project, token }) => {
    const sync = project.githubSync;
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);

    if (!sync || !sync.repo) return null;

    const handlePush = async () => {
        setBusy(true); setErr(null); setResult(null);
        try {
            const res = await axios.post(
                `/api/integrations/github/projects/${project._id}/push`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setResult(res.data);
        } catch (e) {
            const data = e.response?.data;
            if (data?.code === 'GITHUB_REAUTH_REQUIRED') {
                setErr('GitHub access expired — reconnect under Sources.');
            } else {
                setErr(data?.msg || data?.detail || 'Push failed');
            }
        } finally {
            setBusy(false);
        }
    };

    const lastPush = sync.lastPush;
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button type="button" className="btn-q" onClick={handlePush} disabled={busy}>
                <span>{busy ? 'Pushing…' : 'Push to GitHub'}</span>
            </button>
            {result?.prUrl && (
                <a href={result.prUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                    View PR #{result.prNumber}
                </a>
            )}
            {!result && lastPush?.prUrl && (
                <a href={lastPush.prUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, opacity: 0.75 }}>
                    Last PR #{lastPush.prNumber}
                </a>
            )}
            {err && <span style={{ color: '#c33', fontSize: 12 }}>{err}</span>}
        </div>
    );
};

const Header = ({ project, file, totalSegments, totalWords, languages, onSaveSettings, onArchive, onDelete, token }) => {
    const status = statusFor(project);
    const e = ext({ ...project, fileName: file && file.name });
    const crumbParts = splitCrumb(project.category);
    const [settingsOpen, setSettingsOpen] = useState(false);
    return (
        <div className="rs-pd-head">
            <div className={`file-ic ${fileClass(e)}`}>{e}</div>
            <div className="rs-pd-titles">
                {crumbParts.length > 0 && (
                    <div className="crumb-mini">
                        {crumbParts.map((p, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <span className="sep">/</span>}
                                <span>{p}</span>
                            </React.Fragment>
                        ))}
                    </div>
                )}
                <h1>{projectName(project)}</h1>
                <div className="row">
                    <span className={`p-status ${status}`}><span className="pulse" />{status}</span>
                    <span className="dot" />
                    <span>Source: <b>{langDisplay(project.sourceLanguage)}</b></span>
                    <span className="dot" />
                    <span><b>{languages.length}</b> target language{languages.length === 1 ? '' : 's'}</span>
                    <span className="dot" />
                    <span><b>{totalSegments}</b> segments · <b>{totalWords.toLocaleString()}</b> words</span>
                    {project.deadline && (
                        <>
                            <span className="dot" />
                            <span className={dueClass(project.deadline, status) ? 'warn' : undefined}>
                                Due <b>{fmtDateShort(project.deadline)}</b>
                            </span>
                        </>
                    )}
                </div>
            </div>
            <div className="rs-pd-actions">
                <PushToGithubButton project={project} token={token} />
                <button type="button" className="btn-q">{PDIcn.share}<span>Share</span></button>
                <button type="button" className="btn-q">{PDIcn.download}<span>Export</span></button>
                <div className="rs-pd-settings-wrap" style={{ position: 'relative' }}>
                    <button
                        type="button"
                        className={`btn-q ${settingsOpen ? 'on' : ''}`}
                        onClick={() => setSettingsOpen((v) => !v)}
                    >
                        {PDIcn.gear}<span>Settings</span>
                    </button>
                    {settingsOpen && (
                        <ProjectQuickSettings
                            project={project}
                            onSave={onSaveSettings}
                            onArchive={onArchive}
                            onDelete={onDelete}
                            onClose={() => setSettingsOpen(false)}
                            // Drives whether the "Delete language" button is shown
                            // — meaningless for single-language projects.
                            languageCount={languages.length}
                        />
                    )}
                </div>
                <Link to={`/editor/${project._id}`} className="btn-acc" style={{ textDecoration: 'none' }}>
                    {PDIcn.edit}<span>Open editor</span>
                </Link>
            </div>
        </div>
    );
};

const shortName = (n) => {
    if (!n) return '';
    const p = n.trim().split(/\s+/);
    return p[1] ? `${p[0]} ${p[1][0]}.` : p[0];
};

const userInitials = (u) => {
    if (!u) return '?';
    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '';
    if (!name) return '?';
    const parts = name.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const userAvatarPalette = (u) => {
    const s = String(u?._id || u?.id || u?.email || '');
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return `av-${(h % 6) + 1}`;
};

const LangCard = ({ language, defaultOwner, linguists, currentUserId, isPopOpen, onTogglePop, onAssign }) => {
    const navigate = useNavigate();
    const code = langCode(language.code || language);
    const pct = Math.round(language.progress || 0);
    const approvedPct = Math.round(language.approvedPct || 0);
    let status; let cls;
    if (approvedPct >= 100 && pct >= 100) { status = 'Approved'; cls = 'done'; }
    else if (approvedPct > 0) { status = 'In review'; cls = 'review'; }
    else if (pct > 0) { status = 'In progress'; cls = ''; }
    else { status = 'Queued'; cls = ''; }

    const translator = language.translator || null;
    const reviewer = language.reviewer || null;
    void defaultOwner;

    const openProject = () => {
        if (language.projectId) navigate(`/dashboard/projects/${language.projectId}`);
    };

    const handleCardClick = (e) => {
        // Card-wide navigation only when the click is on the header / progress area,
        // not inside the assign button or the popover.
        if (e.target.closest('.lmeta-btn') || e.target.closest('.assign-pop') || e.target.closest('.assign-overlay')) return;
        openProject();
    };

    return (
        <div
            className={`rs-pd-lang ${cls} ${isPopOpen ? 'pop-open' : ''}`}
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') openProject(); }}
        >
            <div className="lh">
                <span className="flag">{code}</span>
                <span className="lpair">{(language.sourceCode || 'EN')} → {code}</span>
                <span className="lpct">{pct}%</span>
            </div>
            <div className="lbar"><span style={{ width: `${pct}%` }} /></div>
            <div className="lphase"><span className="lstatus">{status}</span></div>
            <button
                type="button"
                className={`lmeta-btn ${isPopOpen ? 'on' : ''} ${translator ? '' : 'unassigned'}`}
                onClick={(e) => { e.stopPropagation(); onTogglePop(); }}
            >
                {translator ? (
                    <span className="lmeta-who">
                        <span className={`av-s ${userAvatarPalette(translator)}`}>{userInitials(translator)}</span>
                        <span className="lmeta-name">{shortName(ownerDisplayName(translator))}</span>
                        {reviewer && (
                            <span className="lmeta-rev" title={`Reviewer: ${ownerDisplayName(reviewer)}`}>
                                <span className={`av-s ${userAvatarPalette(reviewer)}`}>{userInitials(reviewer)}</span>
                            </span>
                        )}
                    </span>
                ) : (
                    <span className="lmeta-assign">
                        <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        Assign translator
                    </span>
                )}
                <span className="lmeta-caret">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
            </button>
            {isPopOpen && (
                <AssignPopover
                    lang={{ code, pair: `${(language.sourceCode || 'EN')} → ${code}`, sourceCode: language.sourceCode }}
                    assignment={{ translator: translator ? (translator._id || translator.id || translator) : null,
                                  reviewer: reviewer ? (reviewer._id || reviewer.id || reviewer) : null }}
                    linguists={linguists}
                    currentUserId={currentUserId}
                    onAssign={(role, linguistId) => onAssign(language.projectId, role, linguistId)}
                    onClose={onTogglePop}
                />
            )}
        </div>
    );
};

const Tabs = ({ active, setActive, counts }) => (
    <div className="rs-pd-tabs">
        {TABS.map((name) => (
            <button
                key={name}
                type="button"
                className={`rs-pd-tab ${active === name ? 'on' : ''}`}
                onClick={() => setActive(name)}
            >
                {name}
                {counts[name] != null && <span className="ct">{counts[name]}</span>}
            </button>
        ))}
    </div>
);

const SegmentsPreview = ({ project, file, max = 8 }) => {
    const navigate = useNavigate();
    const sections = (project.sections || []).slice(0, max);
    const translated = project.translatedSections || [];
    const approved = project.approvedSections || [];
    const confidence = project.confidenceScores || [];
    if (!sections.length) {
        return (
            <div className="rs-card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
                <div style={{ fontWeight: 600 }}>No segments yet</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Open the editor to start translating.</div>
            </div>
        );
    }
    const srcCode = langCode(project.sourceLanguage) || 'EN';
    const tgtCode = langCode(project.targetLanguage) || 'TGT';
    return (
        <div className="rs-card" style={{ padding: 0, border: '1px solid var(--line)' }}>
            <div className="rs-card-h" style={{ padding: '18px 20px 12px', marginBottom: 0 }}>
                <h3>Segments</h3>
                <span className="count">{srcCode} → {tgtCode} · showing 1–{sections.length} of {(project.sections || []).length}</span>
                <div className="spacer" />
                <button
                    type="button"
                    className="link"
                    onClick={() => navigate(`/editor/${project._id}`)}
                >
                    Open in editor {PDIcn.arrow}
                </button>
            </div>
            <div className="rs-segs" style={{ border: 0, borderTop: '1px solid var(--line)', borderRadius: 0 }}>
                <div className="rs-seg-h">
                    <span>#</span>
                    <span>Source · {srcCode}</span>
                    <span>Target · {tgtCode}</span>
                    <span style={{ textAlign: 'right' }}>Match · Status</span>
                    <span />
                </div>
                {sections.map((src, i) => {
                    const tgt = translated[i] || '';
                    const isApproved = approved[i] === true || approved[i] === 1;
                    const conf = confidence[i];
                    const tmDisplay = conf != null ? `${Math.round(conf)}%` : '—';
                    const tmCls = conf == null ? 'low' : conf >= 90 ? '' : conf >= 75 ? 'med' : 'low';
                    const status = isApproved ? 'approved' : tgt ? 'translated' : 'pending';
                    return (
                        <div key={i} className={`rs-seg ${status}`}>
                            <span className="sn">{i + 1}</span>
                            <span className="src">{src}</span>
                            <span className="tgt">{tgt || <span style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>Not translated</span>}</span>
                            <span className="meta">
                                <span className={`tm-match ${tmCls}`}>{tmDisplay}</span>
                                <span className={`status-d ${status}`}>{status}</span>
                            </span>
                            <span className="more">{PDIcn.more}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ActivityFeed = ({ history = [] }) => (
    <div className="rs-card">
        <div className="rs-card-h">
            <h3>Activity</h3>
            <div className="spacer" />
        </div>
        <div className="rs-act-feed">
            {history.length === 0 && <div style={{ padding: 16, color: 'var(--ink-3)' }}>No activity recorded yet.</div>}
            {history.map((h, i) => (
                <div key={i} className="act-row">
                    <div className="ah">
                        <span className={`av-s ${avatarPalette(i)}`}>{(h.actor || '?').slice(0, 2).toUpperCase()}</span>
                        <b>{h.actor || 'Someone'}</b>
                        <span className="when">{h.when || ''}</span>
                    </div>
                    <div className="ad">{h.text}{h.target && <> <span className="pill">{h.target}</span></>}</div>
                </div>
            ))}
        </div>
    </div>
);

const Rail = ({
    project, totalSegments, totalWords, memorySegmentCount, languages,
    fuzzyMatches, glossaryTermsCount, lockedPhrasesCount,
}) => {
    const status = statusFor(project);
    const owner = project.createdBy;
    const ownerName = ownerDisplayName(owner);
    const ownerInits = owner && typeof owner === 'object'
        ? ownerInitials(owner, 0)
        : '?';
    return (
        <aside className="rs-pd-rail">
            <div className="rs-card">
                <div className="rs-card-h" style={{ marginBottom: 4 }}><h3>Details</h3></div>
                <div className="rs-pd-info">
                    <div className="row">
                        <span className="k">Owner</span>
                        <span className="v" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            {ownerName ? (
                                <>
                                    <span className={`av-s ${avatarPalette(0)}`} style={{ width: 22, height: 22, fontSize: 10 }}>
                                        {ownerInits}
                                    </span>
                                    {ownerName}
                                </>
                            ) : '—'}
                        </span>
                    </div>
                    {project.assignedTo && project.assignedTo.length > 0 && (
                        <div className="row">
                            <span className="k">Team</span>
                            <span className="v">
                                <span className="av-stack">
                                    {project.assignedTo.slice(0, 5).map((m, i) => (
                                        <span key={i} className={`av-s ${avatarPalette(i + 1)}`}>{ownerInitials(m, i)}</span>
                                    ))}
                                    {project.assignedTo.length > 5 && (
                                        <span
                                            className="av-s"
                                            style={{
                                                background: 'var(--bg-soft)',
                                                color: 'var(--ink-3)',
                                                fontWeight: 700,
                                            }}
                                        >
                                            +{project.assignedTo.length - 5}
                                        </span>
                                    )}
                                </span>
                            </span>
                        </div>
                    )}
                    <div className="row">
                        <span className="k">Source</span>
                        <span className="v">{langDisplay(project.sourceLanguage)}</span>
                    </div>
                    <div className="row">
                        <span className="k">Targets</span>
                        <span className="v">
                            {languages.length > 0
                                ? languages.map((l) => langCode(l.code)).join(' · ')
                                : langDisplay(project.targetLanguage)}
                        </span>
                    </div>
                    {project.createdAt && (
                        <div className="row">
                            <span className="k">Created</span>
                            <span className="v">{new Date(project.createdAt).toLocaleDateString()}</span>
                        </div>
                    )}
                    {project.deadline && (
                        <div className="row">
                            <span className="k">Due</span>
                            <span className={`v ${dueClass(project.deadline, status) || ''}`}>
                                {new Date(project.deadline).toLocaleDateString()}
                            </span>
                        </div>
                    )}
                    <div className="row">
                        <span className="k">Workflow</span>
                        <span className="v">AI draft → translate → review → approve</span>
                    </div>
                    {project.tags && project.tags.length > 0 && (
                        <div className="row">
                            <span className="k">Tags</span>
                            <span className="v">
                                <span className="tagrow">
                                    {project.tags.map((t) => <span key={t} className="tag">{t}</span>)}
                                </span>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="rs-card" style={{ padding: 0 }}>
                <div style={{ padding: '16px 20px 10px' }}>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em' }}>Memory &amp; glossary</h3>
                </div>
                <div className="rs-pd-stats-row">
                    <div className="rs-pd-stat"><span className="v">{memorySegmentCount}</span><span className="l">TM hits</span></div>
                    <div className="rs-pd-stat"><span className="v">{fuzzyMatches}</span><span className="l">Fuzzy matches</span></div>
                    <div className="rs-pd-stat"><span className="v">{glossaryTermsCount}</span><span className="l">Glossary terms</span></div>
                    <div className="rs-pd-stat"><span className="v">{lockedPhrasesCount}</span><span className="l">Locked phrases</span></div>
                </div>
            </div>

            {(project.fileName || project.fileUrl) && (
                <div className="rs-card">
                    <div className="rs-card-h" style={{ marginBottom: 10 }}>
                        <h3>Source</h3>
                    </div>
                    <div className="rs-pd-int">
                        <span className="ic">{(project.fileName || project.fileUrl || 'F').toString().slice(0, 1).toUpperCase()}</span>
                        <div className="body">
                            <b>{project.fileName || (project.fileUrl || '').split('/').pop()}</b>
                            <span>{project.creationSource || 'Upload'}{project.category ? ` · ${project.category}` : ''}</span>
                        </div>
                    </div>
                    <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ink-4)', lineHeight: 1.4 }}>
                        {project.updatedAt ? `Last updated ${new Date(project.updatedAt).toLocaleString()}.` : 'Manually uploaded.'}
                    </div>
                </div>
            )}
        </aside>
    );
};

const CommentRowDisplay = ({ comment, isReply, onClick }) => {
    const author = comment.author || {};
    const authorId = author._id || author.id || '';
    const authorName = `${author.firstName || ''} ${author.lastName || ''}`.trim()
        || author.email || 'User';
    return (
        <div
            className={`rs-ed-cm-item${isReply ? ' reply' : ''}`}
            style={onClick ? { cursor: 'pointer' } : undefined}
            onClick={onClick}
        >
            <div className="ch">
                <span className={`av-s av-${avatarColor(authorId)}`}>
                    {initialsOf(authorName)}
                </span>
                <b>{authorName}</b>
                <span className="when" style={{ marginLeft: 'auto' }}>
                    {relTime(comment.createdAt)}
                    {comment.editedAt ? ' · edited' : ''}
                </span>
            </div>
            <div className="cb">
                {comment.deleted
                    ? <span className="cm-deleted">Comment deleted</span>
                    : renderBody(comment.body, React)}
            </div>
        </div>
    );
};

const CommentsTab = ({ comments, loading, error, onOpenSegment }) => {
    const all = comments || [];
    const tops = all.filter((c) => !c.parentId && !c.deleted);
    const repliesByParent = all.reduce((acc, c) => {
        if (c.parentId) {
            const k = String(c.parentId);
            acc[k] = acc[k] || [];
            acc[k].push(c);
        }
        return acc;
    }, {});
    // Replies returned by the API are newest-first; flip to chronological for display.
    Object.keys(repliesByParent).forEach((k) => {
        repliesByParent[k].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    });

    if (loading) {
        return (
            <div className="rs-card" style={{ padding: 32, color: 'var(--ink-3)' }}>
                Loading comments…
            </div>
        );
    }
    if (error) {
        return (
            <div className="rs-card" style={{ padding: 32, color: 'crimson' }}>
                {error}
            </div>
        );
    }
    if (tops.length === 0) {
        return (
            <div className="rs-card cm-empty" style={{ padding: 48 }}>
                <b>No comments yet</b>
                <span>Comments left on segments in the editor will appear here.</span>
            </div>
        );
    }

    return (
        <div className="rs-card" style={{ padding: 0 }}>
            {tops.map((top) => {
                const segLabel = `Segment ${Number(top.sectionIndex) + 1}`;
                const langLabel = (top.targetLanguage || '').toUpperCase();
                const replies = repliesByParent[String(top._id)] || [];
                const openInEditor = () => onOpenSegment(top.projectId, Number(top.sectionIndex) + 1);
                return (
                    <div key={top._id} className="cm-thread" style={{ margin: 12 }}>
                        <CommentRowDisplay comment={top} isReply={false} onClick={openInEditor} />
                        {replies.map((r) => (
                            <CommentRowDisplay
                                key={r._id}
                                comment={r}
                                isReply
                                onClick={openInEditor}
                            />
                        ))}
                        <div className="cm-actions" style={{ marginTop: 6, marginLeft: 2 }}>
                            {top.resolved && (
                                <span className="cm-resolved-badge">Resolved</span>
                            )}
                            <span className="pill" style={{
                                background: 'var(--bg-soft)', color: 'var(--ink-3)',
                                padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                            }}>{segLabel}</span>
                            {langLabel && (
                                <span className="pill" style={{
                                    background: 'var(--accent-soft)', color: 'var(--accent)',
                                    padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                                    letterSpacing: '0.04em',
                                }}>{langLabel}</span>
                            )}
                            {replies.length > 0 && (
                                <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                                    {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                                </span>
                            )}
                            {top.sourceText && (
                                <span style={{
                                    flex: 1, minWidth: 0, fontSize: 11, color: 'var(--ink-4)',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    textAlign: 'right',
                                }}>
                                    “{top.sourceText}”
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const RushProjectDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = useSelector((s) => s.auth.token);

    const [project, setProject] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [memorySegmentCount, setMemorySegmentCount] = useState(0);
    const [uniqueSegmentCount, setUniqueSegmentCount] = useState(0);
    const [glossaryTermsCount, setGlossaryTermsCount] = useState(0);
    const [lockedPhrasesCount, setLockedPhrasesCount] = useState(0);
    const [activeTab, setActiveTab] = useState('Overview');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [linguists, setLinguists] = useState([]);
    const [openLangId, setOpenLangId] = useState(null);
    const [projectComments, setProjectComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentsError, setCommentsError] = useState(null);

    const fetchProjectComments = useCallback(async () => {
        if (!token || !id) return;
        setCommentsLoading(true);
        setCommentsError(null);
        try {
            const res = await axios.get(`/api/projects/${id}/comments`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setProjectComments(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setCommentsError("Couldn't load comments for this project.");
        } finally {
            setCommentsLoading(false);
        }
    }, [token, id]);

    useEffect(() => { fetchProjectComments(); }, [fetchProjectComments]);

    // Live-refresh the project-wide comments list. The Editor emits comment
    // events to `project_<projectId>` rooms (one per language sub-project), so
    // we join every sibling room and listen there.
    const subProjectIds = useMemo(
        () => (languages || []).map((l) => String(l.projectId)).filter(Boolean),
        [languages],
    );
    const subProjectKey = subProjectIds.join(',');
    useEffect(() => {
        const socket = typeof window !== 'undefined' ? window.__SOCKET__ : null;
        if (!socket || subProjectIds.length === 0) return undefined;
        subProjectIds.forEach((pid) => socket.emit('joinProjectComments', pid));
        socket.on('comment:created', fetchProjectComments);
        socket.on('comment:updated', fetchProjectComments);
        socket.on('comment:deleted', fetchProjectComments);
        return () => {
            subProjectIds.forEach((pid) => {
                try { socket.emit('leaveProjectComments', pid); } catch (e) { /* noop */ }
            });
            socket.off('comment:created', fetchProjectComments);
            socket.off('comment:updated', fetchProjectComments);
            socket.off('comment:deleted', fetchProjectComments);
        };
    // subProjectKey collapses the array dep to a stable string so this only
    // re-runs when the set of language sub-projects actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subProjectKey, fetchProjectComments]);

    const currentUserId = useMemo(() => {
        if (!token) return null;
        try { return jwtDecode(token).id; } catch (e) { return null; }
    }, [token]);

    // Fetch linguist roster once. /api/team is best-effort — failures fall back to an empty pool.
    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/team', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setLinguists(Array.isArray(r.data) ? r.data : []))
            .catch(() => setLinguists([]));
    }, [token]);

    const handleQuickSettingsSave = async (updates) => {
        if (!token || !project) return;
        const headers = { Authorization: `Bearer ${token}` };
        try {
            const res = await axios.put(`/api/projects/${project._id}`, updates, { headers });
            const updated = (res.data && res.data.project) || res.data || null;
            // Merge returned fields back into local state without dropping everything else.
            setProject((prev) => ({ ...(prev || {}), ...(updated || updates) }));
        } catch (e) {
            console.error('Failed to update project settings:', e);
        }
    };

    const handleArchive = async (archive) => {
        if (!token || !project) return;
        const headers = { Authorization: `Bearer ${token}` };
        try {
            await axios.put(`/api/projects/${project._id}/archive`, { archive }, { headers });
            setProject((prev) => ({ ...(prev || {}), archivedAt: archive ? new Date().toISOString() : null }));
        } catch (e) {
            console.error('Failed to archive project:', e);
        }
    };

    // scope = 'group' (default) deletes every language in the project group;
    // scope = 'language' deletes only this one Project record. The backend
    // re-anchors surviving siblings when the deleted Project was the anchor,
    // and returns `nextProjectId` so we can land the user on a sibling rather
    // than dropping them back to AllProjects.
    const handleDelete = async (scope = 'group') => {
        if (!token || !project) return;
        const headers = { Authorization: `Bearer ${token}` };
        try {
            const res = await axios.delete(`/api/projects/${project._id}`, {
                headers,
                params: { scope },
            });
            const nextId = scope === 'language' ? (res.data && res.data.nextProjectId) : null;
            navigate(nextId ? `/dashboard/projects/${nextId}` : '/dashboard/projects');
        } catch (e) {
            console.error('Failed to delete project:', e);
        }
    };

    const handleAssign = async (projectId, role, linguistId) => {
        if (!projectId || !token) return;
        const headers = { Authorization: `Bearer ${token}` };
        try {
            if (role === 'translator') {
                await axios.put(
                    `/api/projects/${projectId}/assign-translator`,
                    { translatorIds: linguistId ? [linguistId] : ['unassign'] },
                    { headers },
                );
            } else {
                await axios.put(
                    `/api/projects/${projectId}/assign-reviewer`,
                    { reviewerId: linguistId || 'unassign' },
                    { headers },
                );
            }
            // Optimistically reflect the change in the per-language list.
            const linguistRecord = (linguists || [])
                .map((t) => t.userId || t)
                .find((u) => String(u._id || u.id) === String(linguistId));
            setLanguages((prev) => prev.map((l) => {
                if (l.projectId !== projectId) return l;
                if (role === 'translator') return { ...l, translator: linguistRecord || null };
                return { ...l, reviewer: linguistRecord || null };
            }));
            // Refetch the project so the merged activityLog (across all language sub-projects)
            // picks up the new assign/unassign entry for the History tab.
            try {
                const refetched = await axios.get(`/api/projects/${id}`, { headers });
                if (refetched.data && refetched.data.project) {
                    setProject((prev) => ({
                        ...(prev || {}),
                        activityLog: refetched.data.project.activityLog || [],
                    }));
                }
            } catch (e) { /* soft */ }
        } catch (e) {
            console.error('Failed to assign linguist:', e);
        }
    };

    // Refetch the languages list (progress + assignments) and activityLog without
    // resetting the page loading state. Used on tab-focus and after socket events
    // so the cards stay live as work happens in the editor or on siblings.
    const refreshLanguages = useCallback(async () => {
        if (!token || !id) return;
        try {
            const res = await axios.get(`/api/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            const p = res.data.project;
            if (res.data.languages) setLanguages(res.data.languages);
            if (p && p.activityLog) {
                setProject((prev) => ({ ...(prev || {}), activityLog: p.activityLog }));
            }
        } catch (e) { /* soft */ }
    }, [token, id]);

    // Refresh whenever the tab regains focus — covers the "translate in the
    // editor, then switch back to project detail" flow.
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') refreshLanguages(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', refreshLanguages);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', refreshLanguages);
        };
    }, [refreshLanguages]);

    // Refresh on segment-save broadcasts. The server emits `segment:saved` into
    // the project group room when any sibling language sub-project gets a
    // segment PUT, so progress/status on every card stays live.
    const projectGroupId = project && String(project.projectGroupId || project._id || '');
    useEffect(() => {
        const socket = typeof window !== 'undefined' ? window.__SOCKET__ : null;
        if (!socket) return undefined;
        if (projectGroupId) socket.emit('joinProjectGroup', projectGroupId);
        socket.on('project:updated', refreshLanguages);
        socket.on('segment:saved', refreshLanguages);
        return () => {
            if (projectGroupId) {
                try { socket.emit('leaveProjectGroup', projectGroupId); } catch (e) { /* noop */ }
            }
            socket.off('project:updated', refreshLanguages);
            socket.off('segment:saved', refreshLanguages);
        };
    }, [refreshLanguages, projectGroupId]);

    useEffect(() => {
        if (!token || !id) return;
        setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };
        axios
            .get(`/api/projects/${id}`, { headers })
            .then((res) => {
                const p = res.data.project;
                setProject(p);
                setUniqueSegmentCount(res.data.uniqueSegmentsCount || 0);
                setMemorySegmentCount(res.data.memorySegmentsCount || 0);
                const langs = res.data.languages || [{ code: p.targetLanguage, projectId: p._id, progress: progressOf(p) }];
                setLanguages(langs);
                setLoading(false);

                // Fetch glossary stats for the rail (best-effort)
                axios
                    .get('/api/glossary', { headers })
                    .then(async (gr) => {
                        const list = Array.isArray(gr.data) ? gr.data : (gr.data.glossaries || []);
                        const counts = await Promise.all(list.map(async (g) => {
                            try {
                                const tr = await axios.get(`/api/glossary/${g._id}/terms`, { headers });
                                return tr.data || [];
                            } catch {
                                return [];
                            }
                        }));
                        const allTerms = counts.flat();
                        setGlossaryTermsCount(allTerms.length);
                        setLockedPhrasesCount(allTerms.filter((t) => t.approved && !t.ignore).length);
                    })
                    .catch(() => {});
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load project.');
                    setLoading(false);
                }
            });
    }, [token, id, dispatch, navigate]);

    const totalSegments = useMemo(() => (project ? (project.sections || []).length : 0), [project]);
    const totalWords = useMemo(() => (project ? (project.wordCount || wordCountFromSections(project.sections)) : 0), [project]);
    const fuzzyMatches = useMemo(() => {
        if (!project) return 0;
        return (project.confidenceScores || []).filter((c) => typeof c === 'number' && c >= 70 && c < 100).length;
    }, [project]);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading project…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    if (!project) return null;

    const fileMeta = {
        name: project.fileName || (project.fileUrl || '').split('/').pop() || project.name,
    };

    const filesCount = ((project.fileUrl || project.fileName) ? 1 : 0)
        + Math.max(0, (project.documentVersions || []).length - 1);
    const openThreadCount = projectComments
        .filter((c) => !c.parentId && !c.deleted && !c.resolved).length;
    const counts = {
        Segments: totalSegments || null,
        Files: filesCount > 0 ? filesCount : null,
        Comments: openThreadCount > 0 ? openThreadCount : null,
    };
    const versionEvents = (project.documentVersions || []).map((v, i, arr) => ({
        ts: v.timestamp ? new Date(v.timestamp).getTime() : 0,
        actor: 'You',
        when: v.timestamp ? new Date(v.timestamp).toLocaleString() : '',
        text: `uploaded version ${i + 1}`,
        target: '',
    }));
    const assignEvents = (project.activityLog || []).map((e) => {
        const at = e.at ? new Date(e.at) : null;
        const actorName = ownerDisplayName(e.actor) || 'Someone';
        const targetName = ownerDisplayName(e.target) || 'a user';
        const langTag = e.meta && e.meta.languageCode ? String(e.meta.languageCode).toUpperCase() : '';
        let text;
        switch (e.kind) {
            case 'assign_translator':   text = `assigned ${targetName} as translator for`; break;
            case 'unassign_translator': text = `unassigned ${targetName} as translator from`; break;
            case 'assign_reviewer':     text = `assigned ${targetName} as reviewer for`; break;
            case 'unassign_reviewer':   text = `unassigned ${targetName} as reviewer from`; break;
            default:                    text = `${e.kind.replace(/_/g, ' ')}`;
        }
        return {
            ts: at ? at.getTime() : 0,
            actor: actorName,
            when: at ? at.toLocaleString() : '',
            text,
            target: langTag,
        };
    });
    const commentEvents = (projectComments || []).map((c) => {
        const at = c.createdAt ? new Date(c.createdAt) : null;
        const author = c.author || {};
        const actorName = `${author.firstName || ''} ${author.lastName || ''}`.trim()
            || author.email || 'Someone';
        const segLabel = `Segment ${Number(c.sectionIndex) + 1}`;
        const langTag = c.targetLanguage ? String(c.targetLanguage).toUpperCase() : '';
        const target = [segLabel, langTag].filter(Boolean).join(' · ');
        const preview = (c.body || '').replace(/\s+/g, ' ').trim();
        const snippet = preview.length > 80 ? `${preview.slice(0, 77)}…` : preview;
        let text;
        if (c.deleted) text = 'deleted a comment on';
        else if (c.parentId) text = `replied "${snippet}" on`;
        else text = `commented "${snippet}" on`;
        return {
            ts: at ? at.getTime() : 0,
            actor: actorName,
            when: at ? at.toLocaleString() : '',
            text,
            target,
        };
    });
    const history = [...versionEvents, ...assignEvents, ...commentEvents]
        .sort((a, b) => b.ts - a.ts)
        .slice(0, 30);

    const rail = (
        <Rail
            project={project}
            totalSegments={totalSegments}
            totalWords={totalWords}
            memorySegmentCount={memorySegmentCount}
            languages={languages}
            fuzzyMatches={fuzzyMatches}
            glossaryTermsCount={glossaryTermsCount}
            lockedPhrasesCount={lockedPhrasesCount}
        />
    );

    return (
        <div className="rs-pd">
            <Header
                project={project}
                file={fileMeta}
                totalSegments={totalSegments}
                totalWords={totalWords}
                languages={languages}
                onSaveSettings={handleQuickSettingsSave}
                onArchive={handleArchive}
                onDelete={handleDelete}
                token={token}
            />

            {languages.length > 0 && (
                <div className="rs-pd-langs">
                    {languages.map((l) => (
                        <LangCard
                            key={l.projectId || l.code}
                            language={{
                                ...l,
                                sourceCode: langCode(project.sourceLanguage) || 'EN',
                            }}
                            defaultOwner={project.createdBy}
                            linguists={linguists}
                            currentUserId={currentUserId}
                            isPopOpen={openLangId === l.projectId}
                            onTogglePop={() => setOpenLangId(openLangId === l.projectId ? null : l.projectId)}
                            onAssign={handleAssign}
                        />
                    ))}
                </div>
            )}

            <Tabs active={activeTab} setActive={setActiveTab} counts={counts} />

            {activeTab === 'Overview' && (
                <div className="rs-pd-body">
                    <div className="rs-pd-main">
                        <SegmentsPreview project={project} file={fileMeta} max={8} />
                        <ActivityFeed history={history} />
                    </div>
                    {rail}
                </div>
            )}

            {activeTab === 'Segments' && (
                <div className="rs-pd-body">
                    <div className="rs-pd-main">
                        <SegmentsPreview project={project} file={fileMeta} max={50} />
                    </div>
                    {rail}
                </div>
            )}

            {activeTab === 'Files' && (
                <FilesTab project={project} languages={languages} />
            )}

            {activeTab === 'Comments' && (
                <CommentsTab
                    comments={projectComments}
                    loading={commentsLoading}
                    error={commentsError}
                    onOpenSegment={(projId, segmentN) => navigate(`/editor/${projId}?segment=${segmentN}`)}
                />
            )}

            {activeTab === 'History' && <ActivityFeed history={history} />}

            {activeTab === 'Settings' && (
                <SettingsTab
                    project={project}
                    languages={languages}
                    onSaved={() => {
                        const headers = { Authorization: `Bearer ${token}` };
                        axios.get(`/api/projects/${project._id}`, { headers })
                            .then((res) => setProject(res.data.project))
                            .catch(() => {});
                    }}
                    onArchived={() => navigate('/dashboard/projects')}
                    onDeleted={() => navigate('/dashboard/projects')}
                />
            )}

            {/* Helpful counts for hidden TM lookups */}
            <div style={{ display: 'none' }} aria-hidden>
                {uniqueSegmentCount}
            </div>
        </div>
    );
};

export default RushProjectDetail;
